import { useEffect, useState } from 'react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { Modal } from '@/shared/ui/Modal'
import type { Flow } from '../../../../functions-webhook/src/automation/flows'
import { PERIOD_LABEL } from './useAutomationStats'
import './FlowStatsModal.css'

/**
 * One flow's email figures, opened from its row — 28 Oct 2026.
 *
 * The row shows sent, opened and clicked; this is the rest: what arrived, what
 * bounced or was marked as spam, who unsubscribed from it, and which links in
 * it were pressed. The counts are the flow's daily stats over the same 30 days
 * as the row; the links are counted from 28 Oct 2026, when the delivery
 * webhook started keeping them for automation emails.
 */
interface LinkRow {
  url: string
  block: string
  clicks: number
  reach: number
  lastAt: Date | null
}

const METRICS: { key: string; label: string; hint?: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered', hint: 'Counted from 28 Oct 2026' },
  { key: 'opened', label: 'Opened', hint: 'Apple Mail opens every message it fetches, so this is a ceiling' },
  { key: 'clicked', label: 'Clicked', hint: 'Messages with at least one link pressed' },
  { key: 'bounced', label: 'Bounced', hint: 'Counted from 28 Oct 2026' },
  { key: 'complained', label: 'Marked as spam', hint: 'Counted from 28 Oct 2026' },
  { key: 'unsubscribed', label: 'Unsubscribed', hint: 'Counted from 28 Oct 2026 — opted out of something from this email' },
  { key: 'unsubscribedAll', label: 'of which from all email' },
]

const pct = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)} %` : '')

export function FlowStatsModal({
  flow,
  counts,
  onClose,
}: {
  flow: Flow
  counts: Record<string, number> | undefined
  onClose: () => void
}) {
  const [links, setLinks] = useState<LinkRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    getDocs(query(collection(db, 'skidaCrmAutomationLinks'), where('flow', '==', flow.id)))
      .then((snap) => {
        if (!live) return
        setLinks(
          snap.docs
            .map((d) => ({
              url: String(d.get('url') ?? ''),
              block: String(d.get('block') ?? ''),
              clicks: Number(d.get('clicks')) || 0,
              reach: Number(d.get('reach')) || 0,
              lastAt: d.get('lastAt')?.toDate?.() ?? null,
            }))
            .sort((a, b) => b.clicks - a.clicks),
        )
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [flow.id])

  const sends = flow.blocks.filter((b) => b.kind === 'send')
  const sum = (metric: string, blockId?: string) =>
    (blockId ? [blockId] : sends.map((b) => b.id)).reduce((n, id) => n + (counts?.[`${id}__${metric}`] ?? 0), 0)
  const sent = sum('sent')
  const delivered = sum('delivered')
  // Rates against what arrived where that is known, else against what was sent.
  const base = delivered || sent

  return (
    <Modal
      title={`${flow.id} ${flow.name}`}
      subtitle={PERIOD_LABEL}
      onClose={onClose}
      width={760}
      footer={
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {!counts ? (
        <p className="empty-note">This flow has not run in the last 30 days.</p>
      ) : (
        <div className="fstats__grid">
          {METRICS.map((m) => {
            const n = sum(m.key)
            return (
              <div className="fstats__kpi" key={m.key} title={m.hint}>
                <span className="fstats__label">{m.label}</span>
                <span className="fstats__value">{n.toLocaleString('en-GB')}</span>
                <span className="fstats__pct">
                  {m.key === 'sent' || m.key === 'unsubscribedAll' ? '' : m.key === 'delivered' ? pct(n, sent) : pct(n, base)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {counts && sends.length > 1 && (
        <table className="fstats__table">
          <thead>
            <tr>
              <th>Email</th>
              {['sent', 'opened', 'clicked', 'unsubscribed'].map((k) => (
                <th key={k} className="fstats__num">
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sends.map((b) => (
              <tr key={b.id}>
                <td>{b.kind === 'send' ? b.subject : b.id}</td>
                {['sent', 'opened', 'clicked', 'unsubscribed'].map((k) => (
                  <td key={k} className="fstats__num">
                    {sum(k, b.id).toLocaleString('en-GB')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 className="fstats__head">Links pressed</h4>
      {error && <p className="recform__error">{error}</p>}
      {!error && links === null && <p className="empty-note">Reading…</p>}
      {links?.length === 0 && (
        <p className="empty-note">
          No link in this flow&rsquo;s email has been pressed since 28 Oct 2026, when they started
          being counted.
        </p>
      )}
      {links && links.length > 0 && (
        <table className="fstats__table">
          <thead>
            <tr>
              <th>Link</th>
              <th className="fstats__num" title="Every press">
                Clicks
              </th>
              <th className="fstats__num" title="Messages with at least one press of this link">
                People
              </th>
              <th className="fstats__num">Last</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={`${l.block}|${l.url}`}>
                <td className="fstats__url">
                  <a href={l.url} target="_blank" rel="noreferrer" title={l.url}>
                    {l.url.replace(/^https?:\/\//, '')}
                  </a>
                </td>
                <td className="fstats__num">{l.clicks.toLocaleString('en-GB')}</td>
                <td className="fstats__num">{l.reach.toLocaleString('en-GB')}</td>
                <td className="fstats__num">{l.lastAt ? l.lastAt.toLocaleDateString('nb-NO') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="micro muted fstats__note">
        Links are grouped without their query string, so the footer&rsquo;s per-reader unsubscribe
        link is one row. Counted since 28 Oct 2026; the figures above are the last 30 days.
      </p>
    </Modal>
  )
}
